import React, { useCallback, useMemo, useRef } from 'react';

import { Col } from '@notino/react-styleguide-v20';

import { ReactFCV } from '@types';

import { useCartPageContext } from '../../../context';
import { IGiftActions } from '../../../model';
import { GiftListItem } from '../GiftListItem';
import { EGiftListItemType } from '../model';

import { Wrapper, WrapperHeadline } from './styled';

interface IGiftsListProps {
  campaign: IGiftActions;
  title: string;
}

export const GiftsList: ReactFCV<IGiftsListProps> = ({
  campaign: { id, idActionBody, choose, assignGiftList },
  title,
}) => {
  const {
    actions: { addGift, removeGift, giftSingleSelectUpdate },
  } = useCartPageContext();

  const isSaving = useRef<boolean>(false);
  const type = choose > 1 ? EGiftListItemType.CheckBox : EGiftListItemType.RadioButton;
  const selectedList = useMemo(() => assignGiftList.filter((x) => x.selected), [assignGiftList]);
  const selectedCount = selectedList.length;

  const singleSelectOnChange = useCallback(
    async (selected: boolean, productId: number, productCode: string, itemId?: number) => {
      if (isSaving.current) return;

      isSaving.current = true;

      if (!selected && itemId) {
        const selectionUpdateResult = await giftSingleSelectUpdate(
          {
            itemId,
            productId,
            campaignId: idActionBody,
            giftListId: id,
            productCode,
          },
          assignGiftList,
        );
        if (selectionUpdateResult) {
          isSaving.current = false;
        }
      } else {
        const addResult = await addGift(
          {
            productId,
            campaignId: idActionBody,
            giftListId: id,
            productCode,
          },
          assignGiftList,
        );
        if (addResult) {
          isSaving.current = false;
        }
      }
    },
    [idActionBody, id, addGift, giftSingleSelectUpdate, assignGiftList],
  );

  const multiSelectOnChange = useCallback(
    (selected: boolean, productId: number, productCode: string, itemId?: number) => {
      if (selected && itemId) {
        removeGift(
          {
            itemId,
            productId,
            giftListId: id,
            productCode,
          },
          assignGiftList,
        );
      } else {
        void addGift(
          {
            productId,
            campaignId: idActionBody,
            giftListId: id,
            productCode,
          },
          assignGiftList,
        );
      }
    },
    [idActionBody, id, removeGift, addGift, assignGiftList],
  );

  return (
    <Wrapper data-testid="gifts-list">
      <Col xs={12}>
        <WrapperHeadline data-testid="gifts-list-title">{title}</WrapperHeadline>
      </Col>
      {assignGiftList.map((gift) => (
        <Col xs={12} sm={6} key={gift.productId}>
          <GiftListItem
            type={type}
            gift={gift}
            disabled={selectedCount === choose && type === EGiftListItemType.CheckBox && !gift.selected}
            onChange={
              type === EGiftListItemType.CheckBox
                ? () => multiSelectOnChange(gift.selected, gift.productId, gift.code, gift.itemId)
                : () => singleSelectOnChange(gift.selected, gift.productId, gift.code, selectedList[0]?.itemId)
            }
          />
        </Col>
      ))}
    </Wrapper>
  );
};
